<script setup lang="ts">
import { computed, onMounted, watch, watchEffect } from "vue";
import { AisInstantSearch, AisSearchBox } from "vue-instantsearch/vue3/es";
import { useRoute, useRouter } from "vue-router";
import type { SearchClientConfig } from "@/composables/types/searchClient";
import { useAlgoliaQueryRouting } from "@/composables/useAlgoliaQueryRouting";
import { DefaultClientConfig, useSearchClient } from "@/composables/useSearchClient";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { ALGOLIA_DEFAULT_HITS_PER_PAGE, ALGOLIA_EXTENDED_HITS_PER_PAGE, API_ENTITY_INFORMATION } from "@/constants";
import { getSingleParam } from "../RegistrationProcess/utils/utils";
import { useMyStudentsAlgoliaIndex } from "./composables/useMyStudentsAlgoliaIndex";
import { useMyStudentsNavigation } from "./composables/useMyStudentsNavigation";
import { useRegisteredFacets } from "./composables/useRegisteredFacets";
import { ALGOLIA_PAGE_PRE_FILTER, PROXY_ENDPOINT_CONFIG } from "./constants";
import MyStudentsFilters from "./MyStudentsFilters.vue";

const route = useRoute();
const router = useRouter();

const { currentFacets } = useRegisteredFacets();
const { defaultIndexName, hasGroupedOrders, sortUrlToIndex, sortIndexToUrl } = useMyStudentsAlgoliaIndex();
const { routing, setIndexName, setAlgoliaFacets, setSortConverters } = useAlgoliaQueryRouting();
const { getSelectedEntityView: selectedEntity } = useTenantEntityManager();

const routePage = computed<string>(() => getSingleParam(route.params.page) ?? "");

const selectedTenantId = computed<string>(() => {
  const selectedEntityId: string | undefined = selectedEntity.value?.id;
  if (selectedEntityId !== undefined) {
    return selectedEntityId;
  }
  return API_ENTITY_INFORMATION.id;
});

const config = computed<SearchClientConfig>(() => {
  const startOfLocalDay = new Date();
  startOfLocalDay.setHours(0, 0, 0, 0);
  const unixTimestamp = Math.floor(startOfLocalDay.getTime() / 1000);

  const hitsPerPage = ["approvals"].includes(routePage.value)
    ? ALGOLIA_EXTENDED_HITS_PER_PAGE
    : ALGOLIA_DEFAULT_HITS_PER_PAGE;

  let additionalFilters = "";
  switch (route.params.page) {
    case "approvals":
      additionalFilters = ALGOLIA_PAGE_PRE_FILTER.approvals;
      additionalFilters += ` AND order.session.session_start_date_ts>${unixTimestamp}`;
      break;
    case "acceptances":
      additionalFilters = `${ALGOLIA_PAGE_PRE_FILTER.acceptance}`;
      additionalFilters += ` AND order.session.session_end_date_ts>${unixTimestamp}`;
      additionalFilters += ` AND order.entity.session_owner_id:${selectedTenantId.value}`;
  }
  return {
    ...DefaultClientConfig,
    addedParams: { has_grouped_orders: hasGroupedOrders.value },
    hitsPerPage: hitsPerPage,
    hiddenFilters: additionalFilters,
    proxyConfig: PROXY_ENDPOINT_CONFIG,
  };
});

const { searchClient, configure } = useSearchClient(config.value);
// Propagate computed-config changes within a mount (e.g. hasGroupedOrders flipping). Cross-mount
// changes are already handled because the parent re-keys this component on path/exchange_mode/entity_id.
watch(config, (next) => configure(next));
const algoliaClient = computed(() => searchClient);

const { currentPage } = useMyStudentsNavigation();

const searchDelay = 2000;

const onSearchTrigger = (helper: any) => {
  if (helper.state) {
    helper.search();
  }
};

const onSearchStateChange = ({ uiState, setUiState }) => {
  setUiState(uiState);
};

// Register routing dependencies as explicit side effects. Previously these lived inside a
// computed which made ordering with the template's prop read non-deterministic; watchEffect
// runs eagerly on setup and re-runs only when its reactive deps change.
watchEffect(() => {
  setAlgoliaFacets(
    currentFacets.value.map((facet: { attribute: string; paramName: string }) => ({
      attribute: facet.attribute,
      paramName: facet.paramName,
    })),
  );
});
watchEffect(() => {
  setIndexName(defaultIndexName.value);
});
setSortConverters({ toUrl: sortIndexToUrl, fromUrl: sortUrlToIndex });

onMounted(() => {
  if (routePage.value !== "acceptances") {
    router.replace({
      query: { ...route.query, acceptance_status: undefined },
    });
  }
});
</script>
<template>
  <ais-instant-search
    v-if="algoliaClient.value"
    :search-client="algoliaClient.value"
    :index-name="defaultIndexName"
    :search-function="onSearchTrigger"
    :routing="routing"
    :on-state-change="onSearchStateChange"
    :stalled-search-delay="searchDelay"
    :insights="false"
    :future="{ preserveSharedStateOnUnmount: true }"
    class="w-full bg-blue-100"
  >
    <div hidden>
      <!-- IMPORTANT:: This hidden field registers and maintains the refinements for the router -->
      <my-students-filters />
      <ais-search-box />
    </div>
    <div class="flex-1" data-testid="content">
      <component :is="currentPage" />
    </div>
  </ais-instant-search>
</template>